import { Controller } from "@hotwired/stimulus"

// Client-side row/column filter for the coach × clearance matrix on
// /admin/coach_clearances. Pure DOM toggling — no fetch — so the filter
// is instant and there's no server-side coordination needed.
//
// Wiring:
//   data-controller="clearances-filter"
//   data-clearances-filter-target="coachInput"       — text input
//   data-clearances-filter-target="clearanceInput"   — text input
//   data-clearances-filter-target="empty"            — element shown when
//                                                       all rows OR all
//                                                       cols are hidden
//   data-coach-name="alice a"        — set on each <tr> in the body
//   data-qual-id / data-qual-name    — set on each header <th> AND every
//                                       body <td> for that column
//
// Matching is substring + case-insensitive on the names baked into the
// data attributes. The cleared input states (default) leave everything
// visible.
export default class extends Controller {
  static targets = [ "coachInput", "clearanceInput", "empty" ]

  declare readonly coachInputTarget: HTMLInputElement
  declare readonly clearanceInputTarget: HTMLInputElement
  declare readonly hasEmptyTarget: boolean
  declare readonly emptyTarget: HTMLElement

  connect() {
    this.apply()
  }

  apply() {
    const coachQuery     = this.coachInputTarget.value.trim().toLowerCase()
    const clearanceQuery = this.clearanceInputTarget.value.trim().toLowerCase()

    // Decide column visibility first so we know which qual_ids survive,
    // then cascade to every body cell tagged with that id.
    const visibleQualIds = new Set<string>()
    let visibleCols = 0
    this.element.querySelectorAll<HTMLElement>("[data-qual-name]").forEach((header) => {
      const name   = header.dataset.qualName ?? ""
      const qualId = header.dataset.qualId   ?? ""
      const show   = clearanceQuery === "" || name.includes(clearanceQuery)
      header.classList.toggle("hidden", !show)
      if (show) {
        visibleQualIds.add(qualId)
        visibleCols += 1
      }
    })
    this.element.querySelectorAll<HTMLElement>("td[data-qual-id]").forEach((cell) => {
      const qualId = cell.dataset.qualId ?? ""
      cell.classList.toggle("hidden", !visibleQualIds.has(qualId))
    })

    // Row visibility: simple coach-name substring match.
    let visibleRows = 0
    this.element.querySelectorAll<HTMLElement>("[data-coach-name]").forEach((row) => {
      const name = row.dataset.coachName ?? ""
      const show = coachQuery === "" || name.includes(coachQuery)
      row.classList.toggle("hidden", !show)
      if (show) visibleRows += 1
    })

    if (this.hasEmptyTarget) {
      this.emptyTarget.classList.toggle("hidden", visibleRows > 0 && visibleCols > 0)
    }
  }

  // Wired to the "Clear" buttons next to each input.
  clearCoach()    { this.coachInputTarget.value = "";    this.apply() }
  clearClearance(){ this.clearanceInputTarget.value = ""; this.apply() }
}
